import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Lock } from "lucide-react";

export default function AdminLogin() {
  const { login, loading, error, isAuthenticated } = useAdminAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });

  // Must be a <Navigate> element — calling navigate() during render is a
  // render-phase update that React can drop, leaving a blank page.
  if (isAuthenticated) return <Navigate to="/ngxp-admin/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await login(form.username, form.password);
    if (ok) navigate("/ngxp-admin/dashboard");
  };

  return (
    <div className="min-h-screen bg-[#0A0B10] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <span className="font-display text-3xl font-bold gradient-text-glow">NGXP</span>
          <p className="mt-1 text-gray-500 text-sm uppercase tracking-widest">Admin Portal</p>
        </div>

        <div className="bg-[#0D0E13] border border-white/5 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#7B2CBF]/20 flex items-center justify-center border border-[#7B2CBF]/30">
              <Lock className="w-5 h-5 text-[#7B2CBF]" />
            </div>
            <div>
              <h1 className="text-white font-semibold text-lg">Sign In</h1>
              <p className="text-gray-500 text-xs">Restricted access</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Username
              </label>
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                required
                autoComplete="username"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#7B2CBF]/60 transition-colors"
                placeholder="Enter username"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Password
              </label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                required
                autoComplete="current-password"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#7B2CBF]/60 transition-colors"
                placeholder="Enter password"
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm bg-red-400/10 border border-red-400/20 rounded-xl px-4 py-3">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary-gradient justify-center mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
